// The Tasks page's own left menu (Volkan, 2026-10-22, after monday.com):
// my / team / opened-by-me lists, project folders with their projects, every
// teammate with open / overdue counts, and automations. The selection lives in
// the URL (`?view=`), so links, back and refresh all keep it. Below `md` the
// menu becomes one select above the content.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, Folder, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { Input } from '../../components/ui/input'
import { Select } from '../../components/ui/select'
import { toast } from '../../lib/toast'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import {
  createFolder,
  deleteFolder,
  getTaskSummary,
  listFolders,
  listPeople,
  listProjects,
  staffTaskKeys,
  updateFolder,
  type ProjectFolder,
  type StaffProject,
} from './api'
import { projectRoute, tasksBasePath, type TaskShell, type TaskView } from './lib'
import { PrivateLock } from './ProjectBits'

/** Page headers next to the menu stay stacked until there is room (2xl). */
export const SIDEBAR_HEADER = 'xl:flex-col 2xl:flex-row 2xl:items-start 2xl:justify-between'

const ITEM =
  'flex min-w-0 items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]'
const ACTIVE = 'bg-[var(--color-surface-muted)] font-semibold'
const ICON =
  'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)]'

type Active = { view: TaskView } | { projectId: string }

/** Folder names are reference data; folder mutations invalidate `staffTaskKeys.all`. */
const REFERENCE_STALE_MS = 5 * 60 * 1000

export function TasksSidebar({ shell, active }: { shell: TaskShell; active: Active }) {
  const t = useT(staffTasksI18n)
  const s = t.sidebar
  const base = tasksBasePath(shell)
  const queryClient = useQueryClient()
  const summary = useQuery({ queryKey: staffTaskKeys.summary, queryFn: getTaskSummary })
  const folders = useQuery({ queryKey: staffTaskKeys.folders, queryFn: listFolders, staleTime: REFERENCE_STALE_MS })
  const projects = useQuery({ queryKey: staffTaskKeys.projects, queryFn: listProjects })
  const people = useQuery({ queryKey: staffTaskKeys.people, queryFn: listPeople })
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [removing, setRemoving] = useState<ProjectFolder | null>(null)

  const refresh = () => void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })
  const onError = () => toast.error(t.toasts.error)
  const add = useMutation({
    mutationFn: () => createFolder(newName.trim()),
    onSuccess: () => {
      setNewName('')
      setAdding(false)
      refresh()
    },
    onError,
  })
  const remove = useMutation({
    mutationFn: (folder: ProjectFolder) => deleteFolder(folder.id),
    onSuccess: () => {
      setRemoving(null)
      refresh()
    },
    onError,
  })

  const isView = (view: TaskView) => 'view' in active && active.view === view
  const link = (view: TaskView, label: React.ReactNode, extra?: React.ReactNode) => (
    <Link
      key={view}
      to={base}
      search={{ view } as never}
      className={cn(ITEM, isView(view) && ACTIVE)}
      aria-current={isView(view) ? 'page' : undefined}
      data-testid={`staff-sidebar-${view}`}
    >
      <span className="truncate">{label}</span>
      {extra}
    </Link>
  )
  const allProjects = projects.data?.items ?? []
  const folderIds = new Set((folders.data?.items ?? []).map((f) => f.id))
  const loose = allProjects.filter((p) => !p.folder_id || !folderIds.has(p.folder_id))
  const mine = summary.data

  return (
    <nav aria-label={s.label} className="flex flex-col gap-0.5 text-sm" data-testid="staff-sidebar">
      {link(
        'mine',
        s.myTasks,
        mine && <Count open={mine.assigned_open} late={mine.assigned_overdue} />,
      )}
      {link('all', s.allTasks)}
      {link('created', s.createdByMe)}

      <SectionTitle
        title={s.folders}
        action={
          <button
            type="button"
            className={ICON}
            aria-label={s.addFolder}
            onClick={() => setAdding((open) => !open)}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
          </button>
        }
      />
      {adding && (
        <form
          className="flex gap-1 px-1 pb-1"
          onSubmit={(e) => {
            e.preventDefault()
            if (newName.trim()) add.mutate()
          }}
        >
          <Input
            autoFocus
            value={newName}
            maxLength={120}
            placeholder={s.folderPlaceholder}
            aria-label={s.folderPlaceholder}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button type="submit" size="sm" disabled={!newName.trim() || add.isPending}>
            {t.form.save}
          </Button>
        </form>
      )}
      {link('projects', s.allProjects)}
      {(folders.data?.items ?? []).map((folder) => (
        <FolderNode
          key={folder.id}
          folder={folder}
          shell={shell}
          active={active}
          projects={allProjects.filter((p) => p.folder_id === folder.id)}
          onRemove={() => setRemoving(folder)}
        />
      ))}
      {loose.length > 0 && (folders.data?.items.length ?? 0) > 0 && (
        <FolderNode
          shell={shell}
          active={active}
          projects={loose}
        />
      )}

      <SectionTitle title={s.people} />
      {(people.data?.items ?? []).map((person) =>
        link(
          `person:${person.user_id}`,
          person.display_name,
          <Count open={person.open_count} late={person.overdue_count} />,
        ),
      )}

      <SectionTitle title={s.other} />
      {link('automations', s.automations)}

      <Dialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{removing ? s.removeFolder(removing.name) : ''}</DialogTitle>
            <DialogDescription>
              {removing ? s.removeFolderConfirm(removing.name) : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoving(null)}>
              {t.form.cancel}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => removing && remove.mutate(removing)}
            >
              {removing ? s.removeFolder(removing.name) : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </nav>
  )
}

function SectionTitle({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="mt-4 flex items-center justify-between px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
      <span>{title}</span>
      {action}
    </div>
  )
}

function Count({ open, late }: { open: number; late: number }) {
  const t = useT(staffTasksI18n)
  return (
    <span
      className="shrink-0 text-xs tabular-nums text-[var(--color-text-muted)]"
      aria-label={t.sidebar.counts(open, late)}
    >
      {open}
      {late > 0 && <span className="font-medium text-[var(--color-danger)]"> · {late}</span>}
    </span>
  )
}

/** A folder with its projects; without `folder` it is the "No folder" group. */
function FolderNode({
  folder,
  shell,
  active,
  projects,
  onRemove,
}: {
  folder?: ProjectFolder
  shell: TaskShell
  active: Active
  projects: StaffProject[]
  onRemove?: () => void
}) {
  const t = useT(staffTasksI18n)
  const s = t.sidebar
  const queryClient = useQueryClient()
  const view: TaskView = folder ? `folder:${folder.id}` : 'folder:none'
  const holdsActive = 'projectId' in active && projects.some((p) => p.id === active.projectId)
  const [open, setOpen] = useState(holdsActive || ('view' in active && active.view === view))
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(folder?.name ?? '')
  const rename = useMutation({
    mutationFn: (id: string) => updateFolder(id, { name: name.trim() }),
    onSuccess: () => {
      setEditing(false)
      void queryClient.invalidateQueries({ queryKey: staffTaskKeys.folders })
    },
    onError: () => toast.error(t.toasts.error),
  })
  const label = folder?.name ?? s.noFolder
  const isActive = 'view' in active && active.view === view

  return (
    <div data-testid={`staff-sidebar-folder-${folder?.id ?? 'none'}`}>
      {editing && folder ? (
        <form
          className="flex gap-1 px-1 py-0.5"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) rename.mutate(folder.id)
          }}
        >
          <Input
            autoFocus
            value={name}
            maxLength={120}
            aria-label={s.renameFolder(folder.name)}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" size="sm" disabled={!name.trim() || rename.isPending}>
            {t.form.save}
          </Button>
        </form>
      ) : (
        <div className={cn('group', ITEM, 'py-1 pl-0.5', isActive && ACTIVE)}>
          <button
            type="button"
            className={ICON}
            aria-expanded={open}
            aria-label={label}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>
          <Link
            to={tasksBasePath(shell)}
            search={{ view } as never}
            className={cn('flex min-w-0 flex-1 items-center gap-1.5', !folder && 'text-[var(--color-text-muted)]')}
          >
            <Folder className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">{label}</span>
          </Link>
          {folder && (
            <span className="flex shrink-0 items-center opacity-0 focus-within:opacity-100 group-hover:opacity-100">
              <button
                type="button"
                className={ICON}
                aria-label={s.renameFolder(folder.name)}
                onClick={() => {
                  setName(folder.name)
                  setEditing(true)
                }}
              >
                <Pencil className="h-3 w-3" aria-hidden />
              </button>
              <button
                type="button"
                className={ICON}
                aria-label={s.removeFolder(folder.name)}
                onClick={onRemove}
              >
                <Trash2 className="h-3 w-3" aria-hidden />
              </button>
            </span>
          )}
          <span className="shrink-0 text-xs tabular-nums text-[var(--color-text-muted)] group-hover:hidden">
            {projects.length}
          </span>
        </div>
      )}
      {open && (
        <div className="flex flex-col gap-0.5 pl-6">
          {projects.map((project) => {
            const current = 'projectId' in active && active.projectId === project.id
            return (
              <Link
                key={project.id}
                {...projectRoute(shell, project.id)}
                className={cn(ITEM, 'py-1', current && ACTIVE)}
                aria-current={current ? 'page' : undefined}
              >
                <span className="flex min-w-0 items-center gap-1">
                  <span className="truncate">{project.name}</span>
                  {project.is_private && (
                    <PrivateLock memberCount={project.members?.length} className="h-3 w-3" />
                  )}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-[var(--color-text-muted)]">
                  {project.tasks_done}/{project.tasks_total}
                </span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** Below `md`: the same menu as one select above the content. */
export function TasksViewPicker({ shell, active }: { shell: TaskShell; active: Active }) {
  const t = useT(staffTasksI18n)
  const s = t.sidebar
  const navigate = useNavigate()
  const folders = useQuery({ queryKey: staffTaskKeys.folders, queryFn: listFolders, staleTime: REFERENCE_STALE_MS })
  const people = useQuery({ queryKey: staffTaskKeys.people, queryFn: listPeople })
  const value = 'view' in active ? active.view : 'projects'
  return (
    <Select
      aria-label={s.jumpTo}
      value={value}
      onChange={(e) =>
        void navigate({ to: tasksBasePath(shell), search: { view: e.target.value } as never })
      }
    >
      <option value="mine">{s.myTasks}</option>
      <option value="all">{s.allTasks}</option>
      <option value="created">{s.createdByMe}</option>
      <option value="projects">{s.allProjects}</option>
      {(folders.data?.items ?? []).map((folder) => (
        <option key={folder.id} value={`folder:${folder.id}`}>
          {s.folders}: {folder.name}
        </option>
      ))}
      {(people.data?.items ?? []).map((person) => (
        <option key={person.user_id} value={`person:${person.user_id}`}>
          {s.people}: {person.display_name}
        </option>
      ))}
      <option value="automations">{s.automations}</option>
    </Select>
  )
}

/** Sidebar + content; the sidebar turns into the picker on small screens. */
export function TasksLayout({
  shell,
  active,
  compact = false,
  children,
}: {
  shell: TaskShell
  active: Active
  compact?: boolean
  children: React.ReactNode
}) {
  return (
    <div className={cn(
      'grid gap-6',
      compact ? 'lg:grid-cols-[14rem_minmax(0,1fr)]' : 'md:grid-cols-[15rem_minmax(0,1fr)]',
    )}>
      <aside className={compact ? 'hidden lg:block' : 'hidden md:block'}>
        <div className="sticky top-4">
          <TasksSidebar shell={shell} active={active} />
        </div>
      </aside>
      <div className="flex min-w-0 flex-col gap-4">
        <div className={compact ? 'lg:hidden' : 'md:hidden'}>
          <TasksViewPicker shell={shell} active={active} />
        </div>
        {children}
      </div>
    </div>
  )
}
